// Duels and rooms display helpers (contracts/phase4.md §B, §C).

import type { DuelRow, DuelStatus } from "../api/schemas";

export type RatingTier = "Slate" | "Amber" | "Teal" | "Violet" | "Laser";

const TIERS: { name: RatingTier; from: number }[] = [
  { name: "Laser", from: 1600 },
  { name: "Violet", from: 1500 },
  { name: "Teal", from: 1400 },
  { name: "Amber", from: 1300 },
  { name: "Slate", from: -Infinity },
];

/**
 * Tiers every 100 points (Slate under 1300 … Laser 1600+), each split every 33 points into III (the
 * entry division), II and I (the top), as backend/api computes them.
 */
export function tierFor(rating: number): { tier: RatingTier; division: "I" | "II" | "III" } {
  const r = Math.round(rating);
  const tier = TIERS.find((t) => r >= t.from)!.name;
  const floor = tier === "Slate" ? 1200 : TIERS.find((t) => t.name === tier)!.from;
  const step = Math.max(0, Math.min(2, Math.floor((r - floor) / 33)));
  return { tier, division: (["III", "II", "I"] as const)[step]! };
}

/** Tier → its colour token (board G12's ladder). */
export const TIER_COLOR: Record<RatingTier, string> = {
  Slate: "var(--color-text-quaternary)",
  Amber: "var(--color-state-amber-label)",
  Teal: "var(--color-state-teams)",
  Violet: "var(--color-state-violet)",
  Laser: "var(--color-accent)",
};

export const DUEL_STATUS_TONE: Record<DuelStatus, string> = {
  queued: "neutral",
  pending: "neutral",
  choosing: "violet",
  active: "info",
  settled: "success",
  declined: "muted",
  expired: "warning",
  cancelled: "muted",
};

export const MODE_LABEL: Record<DuelRow["mode"], string> = { friend: "friend", ladder: "ladder", series: "series" };

/** "+18", "−7", "±0". */
export const fmtDelta = (d: number | null | undefined) => (d === null || d === undefined ? "—" : d > 0 ? `+${d}` : d < 0 ? `−${Math.abs(d)}` : "±0");

/** "91–87", or "—" before anyone has a score. */
export function scoreLine(d: DuelRow): string {
  const a = d.challenger.forfeit ? "F" : d.challenger.score;
  const b = d.opponent ? (d.opponent.forfeit ? "F" : d.opponent.score) : null;
  if (a === null && (b === null || b === undefined)) return "—";
  return `${a ?? "·"}–${b ?? "·"}`;
}

export const WINDOW_LABEL: Record<"24h" | "week", string> = { "24h": "24 h", week: "week" };
